import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, MagnifyingGlass, X, User, Megaphone, Lightning, CalendarCheck,
  ChatCircleDots, Robot, Pulse, WhatsappLogo, CaretDown, Gear, SignOut, IdentificationCard,
  Compass, Wallet, UsersThree, ClockCounterClockwise, Star, Question,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLiveCounts } from "@/contexts/LiveCountsContext";
import { replayOnboardingTour } from "@/components/OnboardingTour";
import { canAccess, isAdmin, ROLE_LABELS } from "@/lib/permissions";
import SectorPreviewSwitcher from "@/components/SectorPreviewSwitcher";

// ------- Notifications dropdown -------

const NOTIF_ACTIONS = {
  "decision.acted":         { icon: Pulse,         color: "text-emerald-600", label: (l) => `Decisão IA: ${l.detail?.rule_name || l.detail?.rule_code || "regra"}` },
  "campaign.executed":      { icon: Megaphone,     color: "text-blue-600",    label: (l) => `Campanha disparada (${l.detail?.sent || 0} envios)` },
  "campaign.create":        { icon: Megaphone,     color: "text-slate-500",   label: (l) => `Campanha agendada: ${l.detail?.name || ""}` },
  "appointment.create":     { icon: CalendarCheck, color: "text-indigo-600",  label: () => "Novo agendamento" },
  "appointment.update":     { icon: CalendarCheck, color: "text-slate-500",   label: () => "Agendamento atualizado" },
  "customer.create":        { icon: User,          color: "text-slate-500",   label: () => "Novo cliente cadastrado" },
  "lead.create":            { icon: User,          color: "text-amber-600",   label: (l) => `Novo lead${l.detail?.phone ? `: ${l.detail.phone}` : ""}` },
  // IA-driven creations — distinct icon/label so the operator immediately
  // sees that the IA did the work and can audit / open the record.
  "lead.auto_promoted":     { icon: Robot,         color: "text-emerald-600", label: (l) => `Novo cliente cadastrado pela IA${l.detail?.phone ? ` · ${l.detail.phone}` : ""}` },
  "vehicle_capture.confirmed": { icon: Robot,      color: "text-indigo-600",  label: (l) => `Veículo cadastrado pela IA${l.detail?.plate ? ` · ${l.detail.plate}` : (l.detail?.label ? ` · ${l.detail.label}` : "")}` },
  "vehicle_capture.completed_with_text": { icon: Robot, color: "text-indigo-600", label: (l) => `Veículo cadastrado pela IA${l.detail?.plate ? ` · ${l.detail.plate}` : (l.detail?.label ? ` · ${l.detail.label}` : "")}` },
  "message.inbound":        { icon: ChatCircleDots, color: "text-slate-400",  label: (l) => l.detail?.handoff_requested ? "Cliente pediu atendimento humano" : "Nova mensagem recebida" },
  "campaign.cancel":        { icon: X,             color: "text-rose-600",    label: () => "Campanha cancelada" },
  "review.rated":           { icon: Star,          color: "text-amber-500",   label: (l) => `Nova avaliação ${"⭐".repeat(l.detail?.rating || 0)} — ${l.detail?.customer_name || "Cliente"}` },
};

const RELEVANT_ACTIONS = new Set([
  "decision.acted", "campaign.executed", "campaign.create", "campaign.cancel",
  "appointment.create", "appointment.update",
  "customer.create", "lead.create",
  "lead.auto_promoted", "vehicle_capture.confirmed", "vehicle_capture.completed_with_text",
  "review.rated",
]);

function timeAgo(iso) {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  return `${d}d`;
}

function NotificationsDropdown({ onClose }) {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();
  // Awaiting human + negative reviews + pending questions come from the
  // shared LiveCounts poller so we don't duplicate network traffic with
  // the bell badge.
  const { awaitingHumanItems, negativeReviewsItems, pendingQuestionsItems } = useLiveCounts();
  const awaiting = awaitingHumanItems;
  const negativeReviews = negativeReviewsItems;
  const pendingQs = pendingQuestionsItems;

  useEffect(() => {
    // Audit feed is dropdown-only (the bell badge doesn't need it), so
    // we keep that fetch local to this component. The other two feeds
    // (awaiting + negative_reviews) now come from the context above.
    api.get("/audit?limit=80").catch(() => ({ data: [] })).then((auditRes) => {
      const filtered = (auditRes.data || []).filter((l) => {
        if (RELEVANT_ACTIONS.has(l.action)) return true;
        if (l.action === "message.inbound" && l.detail?.handoff_requested) return true;
        return false;
      }).slice(0, 15);
      setItems(filtered);
    });
  }, []);

  const totalUrgent = (awaiting?.length || 0) + (negativeReviews?.length || 0) + (pendingQs?.length || 0);
  const goToConversation = (cid) => {
    onClose();
    navigate(`/app/conversas?cid=${cid}`);
  };
  const goToNegativeReview = (reviewId) => {
    onClose();
    navigate(`/app/avaliacoes?focus=${reviewId}`);
  };
  const goToAllReviews = () => {
    onClose();
    navigate("/app/avaliacoes");
  };
  // Iter-71.6 — deep-link: click on a pending-question notif must open
  // that EXACT question's drawer, not just the list page (Marcelo's UX
  // complaint: "não perder tempo procurando depois de clicar").
  const goToPendingQuestion = (pqId) => {
    onClose();
    navigate(`/app/perguntas-pendentes?focus=${pqId}`);
  };

  return (
    <div className="absolute top-12 right-0 w-96 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="notifications-dropdown">
      <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h4 className="font-heading text-sm font-semibold text-slate-900">Notificações</h4>
          <p className="text-[11px] text-slate-500">
            {totalUrgent > 0
              ? <span className="text-rose-600 font-medium">{totalUrgent} {totalUrgent === 1 ? "item urgente" : "itens urgentes"}</span>
              : "Últimos eventos do seu workspace"}
          </p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="notifications-close">
          <X size={14} />
        </button>
      </header>

      {/* ─── URGENT: pending questions — IA pediu ajuda ao operador ─── */}
      {pendingQs && pendingQs.length > 0 && (
        <div className="border-b border-slate-100" data-testid="notifications-pending-questions">
          <div className="px-4 pt-3 pb-1.5 text-[10px] uppercase tracking-[0.16em] text-amber-700 font-semibold flex items-center gap-1.5">
            <Question size={11} weight="fill" className="text-amber-600" />
            A IA pediu ajuda · alimente o cérebro
          </div>
          {pendingQs.slice(0, 5).map((pq) => (
            <button
              key={pq.id}
              onClick={() => goToPendingQuestion(pq.id)}
              data-testid={`notif-pending-${pq.id}`}
              className="w-full text-left px-4 py-3 hover:bg-amber-50/60 border-t border-slate-100 first:border-t-0 transition-colors flex items-start gap-3"
            >
              <div className="w-7 h-7 rounded-md bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0">
                <Question size={13} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-slate-900 truncate">
                  {pq.customer_name || "Cliente"}
                </div>
                <div className="text-[11.5px] text-slate-600 truncate mt-0.5 italic">
                  &ldquo;{pq.question_text}&rdquo;
                </div>
              </div>
              <div className="text-[10px] text-amber-700 font-semibold tabular-nums shrink-0 mt-0.5">
                {timeAgo(pq.created_at)}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* ─── URGENT: negative reviews (1-2★) needing recovery action ─── */}
      {negativeReviews && negativeReviews.length > 0 && (
        <div className="border-b border-slate-100" data-testid="notifications-negative-reviews">
          <div className="px-4 pt-3 pb-1.5 text-[10px] uppercase tracking-[0.16em] text-rose-600 font-semibold flex items-center gap-1.5">
            <Star size={11} weight="fill" className="text-rose-500" />
            Avaliações negativas · resgate o cliente
          </div>
          {negativeReviews.slice(0, 5).map((alert) => (
            <button
              key={alert.id}
              onClick={() => goToNegativeReview(alert.detail?.review_id || alert.target_id)}
              data-testid={`notif-negative-${alert.detail?.review_id || alert.target_id}`}
              className="w-full text-left px-4 py-3 hover:bg-rose-50/60 border-t border-slate-100 first:border-t-0 transition-colors flex items-start gap-3"
            >
              <div className="w-7 h-7 rounded-md bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
                <Star size={13} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-slate-900 truncate">
                  {"⭐".repeat(alert.detail?.rating || 1)} — {alert.detail?.customer_name || "Cliente"}
                </div>
                {alert.detail?.comment && (
                  <div className="text-[11.5px] text-slate-600 truncate mt-0.5 italic">
                    "{alert.detail.comment}"
                  </div>
                )}
                {!alert.detail?.comment && alert.detail?.vehicle_label && (
                  <div className="text-[11.5px] text-slate-500 truncate mt-0.5">
                    {alert.detail.vehicle_label}
                  </div>
                )}
              </div>
              <div className="text-[10px] text-rose-600 font-semibold tabular-nums shrink-0 mt-0.5">
                há {timeAgo(alert.created_at)}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* ─── URGENT: conversations waiting for a human reply ─────── */}
      {awaiting && awaiting.length > 0 && (
        <div className="border-b border-slate-100" data-testid="notifications-awaiting">
          <div className="px-4 pt-3 pb-1.5 text-[10px] uppercase tracking-[0.16em] text-rose-600 font-semibold flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
            Aguardando você responder
          </div>
          {awaiting.slice(0, 5).map((c) => (
            <button
              key={c.conversation_id}
              onClick={() => goToConversation(c.conversation_id)}
              data-testid={`notif-awaiting-${c.conversation_id}`}
              className="w-full text-left px-4 py-3 hover:bg-rose-50/40 border-t border-slate-100 first:border-t-0 transition-colors flex items-start gap-3"
            >
              <div className="w-7 h-7 rounded-md bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
                <ChatCircleDots size={13} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-slate-900 truncate">
                  {c.name && c.name !== "—" ? c.name : (c.phone || "Cliente sem identificação")}
                </div>
                <div className="text-[12px] text-slate-600 truncate mt-0.5">
                  {c.last_message ? `"${c.last_message}"` : "Mensagem recebida"}
                </div>
              </div>
              <div className="text-[10px] text-rose-600 font-semibold tabular-nums shrink-0 mt-0.5">
                há {timeAgo(c.waiting_since)}
              </div>
            </button>
          ))}
        </div>
      )}
      <div className="max-h-[420px] overflow-y-auto">
        {items === null && (
          <div className="px-4 py-8 text-center text-sm text-slate-400">Carregando…</div>
        )}
        {items?.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-slate-400">
            Nenhuma notificação por aqui ainda.
          </div>
        )}
        {items?.map((item) => {
          const cfg = NOTIF_ACTIONS[item.action] || {
            icon: Lightning, color: "text-slate-400",
            label: () => item.action,
          };
          const Icon = cfg.icon;
          // Decide click target per action type — IA-created records go
          // straight to the customer detail, avaliações to /avaliacoes.
          let onClick = null;
          if (item.action === "review.rated") {
            onClick = goToAllReviews;
          } else if (item.action === "lead.auto_promoted" && item.detail?.customer_id) {
            const cid = item.detail.customer_id;
            onClick = () => { onClose(); navigate(`/app/clientes?focus_id=${cid}`); };
          } else if ((item.action === "vehicle_capture.confirmed" || item.action === "vehicle_capture.completed_with_text") && item.detail?.customer_id) {
            const cid = item.detail.customer_id;
            onClick = () => { onClose(); navigate(`/app/clientes?focus_id=${cid}`); };
          } else if (item.action === "customer.create" && item.target_id) {
            const cid = item.target_id;
            onClick = () => { onClose(); navigate(`/app/clientes?focus_id=${cid}`); };
          } else if (item.action === "appointment.create" && item.target_id) {
            onClick = () => { onClose(); navigate(`/app/agendamentos?open=${item.target_id}`); };
          }
          return (
            <button
              key={item.id}
              type="button"
              onClick={onClick}
              disabled={!onClick}
              className={`w-full text-left px-4 py-3 border-b border-slate-50 last:border-b-0 flex items-start gap-3 ${onClick ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"}`}
              data-testid={`notification-item-${item.action}`}
            >
              <Icon size={16} weight="regular" className={`${cfg.color} mt-0.5 shrink-0`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-800 leading-snug">{cfg.label(item)}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{timeAgo(item.created_at)}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ------- Search dropdown -------

function SearchDropdown({ onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }
    const tid = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/customers", { params: { search: q } });
        setResults((data || []).slice(0, 8));
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(tid);
  }, [q]);

  const goToCustomer = (c) => {
    navigate(`/app/clientes?focus=${encodeURIComponent(c.phone)}`);
    onClose();
  };

  return (
    <div className="absolute top-12 right-0 w-96 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="search-dropdown">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <MagnifyingGlass size={14} weight="regular" className="text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar cliente por nome, telefone, e-mail…"
          data-testid="search-input"
          className="flex-1 outline-none text-sm placeholder:text-slate-400"
          onKeyDown={(e) => e.key === "Escape" && onClose()}
        />
        <button onClick={onClose} className="text-slate-400 hover:text-slate-900">
          <X size={12} />
        </button>
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        {q.length < 2 && (
          <div className="px-4 py-8 text-center text-sm text-slate-400">
            Digite ao menos 2 caracteres para buscar.
          </div>
        )}
        {q.length >= 2 && loading && (
          <div className="px-4 py-8 text-center text-sm text-slate-400">Buscando…</div>
        )}
        {q.length >= 2 && !loading && results.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-slate-400">
            Nenhum cliente encontrado para “{q}”.
          </div>
        )}
        {results.length > 0 && (
          <>
            <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.16em] text-slate-400 font-medium">
              Clientes ({results.length})
            </div>
            {results.map((c) => (
              <button
                key={c.id}
                onClick={() => goToCustomer(c)}
                data-testid={`search-result-${c.id}`}
                className="w-full px-4 py-2.5 hover:bg-slate-50 flex items-center gap-3 text-left"
              >
                <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[11px] font-semibold shrink-0">
                  {(c.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-900 truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{c.phone}{c.email && ` · ${c.email}`}</div>
                </div>
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ------- WhatsApp status pill -------

// Cache em memória compartilhado entre renders/montagens para evitar
// múltiplas chamadas ao endpoint quando o usuário troca de rota.
const _waStatusCache = { data: null, fetchedAt: 0 };
const _WA_CACHE_TTL_MS = 30_000;

function WhatsAppStatusPill() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(_waStatusCache.data);
  const [loading, setLoading] = useState(!_waStatusCache.data);

  const fetchStatus = async (silent = false) => {
    // Usa cache se ainda fresco
    if (Date.now() - _waStatusCache.fetchedAt < _WA_CACHE_TTL_MS && _waStatusCache.data) {
      setStatus(_waStatusCache.data);
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/integrations/zapi/status");
      _waStatusCache.data = data;
      _waStatusCache.fetchedAt = Date.now();
      setStatus(data);
    } catch {
      _waStatusCache.data = { enabled: false, configured: false, connected: false };
      _waStatusCache.fetchedAt = Date.now();
      setStatus(_waStatusCache.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchStatus(true);
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  // 3 estados visuais: conectado, desconectado (configurado mas não conecta) e não configurado.
  let dotClass = "bg-slate-300";
  let ringClass = "";
  let label = "WhatsApp";
  let tooltip = "Status do WhatsApp";
  let textClass = "text-slate-600";

  if (loading) {
    label = "Verificando…";
    tooltip = "Verificando conexão do WhatsApp";
  } else if (!status?.configured) {
    dotClass = "bg-slate-300";
    label = "WhatsApp não configurado";
    tooltip = "Configure a integração com WhatsApp para começar a receber mensagens";
  } else if (status?.connected) {
    dotClass = "bg-slate-900";
    ringClass = "ring-2 ring-slate-200";
    label = "WhatsApp conectado";
    tooltip = `Conectado · ${status.session || "ativo"}`;
    textClass = "text-slate-700";
  } else {
    dotClass = "bg-rose-500";
    ringClass = "ring-2 ring-rose-100";
    label = "WhatsApp desconectado";
    tooltip = "O WhatsApp aparece configurado mas não está respondendo. Clique para verificar.";
    textClass = "text-rose-700";
  }

  return (
    <button
      onClick={() => navigate("/app/integracoes")}
      data-testid="topbar-wa-status"
      title={tooltip}
      className={`hidden md:inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-[12px] font-medium ${textClass}`}
    >
      <span className="relative flex h-2 w-2">
        {status?.connected && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-slate-400 opacity-60" />
        )}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${dotClass} ${ringClass}`} />
      </span>
      <span className="hidden lg:inline tracking-tight">{label}</span>
      <WhatsappLogo size={12} weight="regular" className="lg:hidden text-slate-500" />
    </button>
  );
}

// ------- Topbar -------

export default function Topbar({ title, subtitle, actions }) {
  const { user, tenant, logout } = useAuth();
  const nav = useNavigate();
  const [openMenu, setOpenMenu] = useState(null); // 'search' | 'notif' | 'account' | null
  const wrapperRef = useRef(null);

  // Shared LiveCounts poll — same single source the Sidebar badges read
  // from. Avoids duplicate polling and keeps the badge count consistent
  // across the entire chrome.
  const { awaitingHumanCount: awaitingCount, negativeReviewsCount, pendingQuestionsCount } = useLiveCounts();

  const urgentBadgeCount = awaitingCount + negativeReviewsCount + pendingQuestionsCount;

  // close on outside click
  useEffect(() => {
    if (!openMenu) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openMenu]);

  // close on Escape
  useEffect(() => {
    if (!openMenu) return;
    const handler = (e) => e.key === "Escape" && setOpenMenu(null);
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [openMenu]);

  const initials =
    (user?.name || "U")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0])
      .join("")
      .toUpperCase() || "U";

  return (
    <header
      className="min-h-20 border-b border-slate-200 bg-white sticky top-0 z-30 flex flex-wrap items-center justify-between gap-y-3 px-6 md:px-8 py-3"
      data-testid="app-topbar"
    >
      <div className="min-w-0 mr-3">
        <h1 className="font-heading text-2xl md:text-[28px] font-semibold text-slate-900 tracking-tight leading-tight whitespace-nowrap">
          {title}
        </h1>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5 leading-snug truncate max-w-[64ch]">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-3 ml-auto" ref={wrapperRef}>
        {actions}
        <SectorPreviewSwitcher />
        <WhatsAppStatusPill />
        <div className="relative">
          <button
            onClick={() => setOpenMenu((v) => (v === "search" ? null : "search"))}
            data-testid="topbar-search"
            className={`w-9 h-9 rounded-md flex items-center justify-center transition-colors ${openMenu === "search" ? "bg-slate-100 text-slate-900" : "hover:bg-slate-100 text-slate-500 hover:text-slate-900"}`}
            aria-label="Buscar"
          >
            <MagnifyingGlass size={16} weight="regular" />
          </button>
          {openMenu === "search" && <SearchDropdown onClose={() => setOpenMenu(null)} />}
        </div>
        <div className="relative">
          <button
            onClick={() => setOpenMenu((v) => (v === "notif" ? null : "notif"))}
            data-testid="topbar-bell"
            className={`w-9 h-9 rounded-md flex items-center justify-center transition-colors relative ${openMenu === "notif" ? "bg-slate-100 text-slate-900" : "hover:bg-slate-100 text-slate-500 hover:text-slate-900"}`}
            aria-label="Notificações"
          >
            <Bell size={16} weight="regular" />
            {urgentBadgeCount > 0 && (
              <span
                data-testid="topbar-bell-badge"
                title={`${awaitingCount} aguardando · ${negativeReviewsCount} avaliações negativas · ${pendingQuestionsCount} pergunta(s) pra alimentar a IA`}
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white"
              >
                {urgentBadgeCount > 9 ? "9+" : urgentBadgeCount}
              </span>
            )}
          </button>
          {openMenu === "notif" && <NotificationsDropdown onClose={() => setOpenMenu(null)} />}
        </div>
        <div className="relative pl-3 border-l border-slate-200">
          <button
            type="button"
            onClick={() => setOpenMenu((v) => (v === "account" ? null : "account"))}
            data-testid="topbar-account-trigger"
            className={`flex items-center gap-3 px-2 py-1.5 rounded-lg transition-colors ${
              openMenu === "account" ? "bg-slate-100" : "hover:bg-slate-50"
            }`}
            aria-label="Minha conta"
          >
            <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-[11px] font-semibold tracking-wider">
              {initials}
            </div>
            <div className="hidden md:block text-left">
              <div className="text-sm font-medium text-slate-900 leading-none">{user?.name}</div>
              <div className="text-[11px] text-slate-500 mt-1 truncate max-w-[180px]">{tenant?.name}</div>
            </div>
            <CaretDown size={11} className="text-slate-400 hidden md:block" />
          </button>
          {openMenu === "account" && (
            <div
              className="absolute top-full right-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden"
              data-testid="account-menu"
            >
              <div className="px-4 py-3.5 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white">
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-medium">Conta</div>
                <div className="text-sm font-medium text-slate-900 mt-1 truncate" title={user?.email}>{user?.email}</div>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <span className="inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-900 text-white font-semibold">
                    {ROLE_LABELS[user?.role] || user?.role}
                  </span>
                  {tenant?.name && (
                    <span className="text-[11px] text-slate-500 truncate" title={tenant.name}>· {tenant.name}</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setOpenMenu(null); nav("/app/minha-conta"); }}
                data-testid="account-menu-settings"
                className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5"
              >
                <IdentificationCard size={14} className="text-slate-500" />
                Meu perfil
              </button>
              {canAccess(user, "team") && (
                <button
                  type="button"
                  onClick={() => { setOpenMenu(null); nav("/app/equipe"); }}
                  data-testid="account-menu-team"
                  className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5"
                >
                  <UsersThree size={14} className="text-slate-500" />
                  Equipe
                </button>
              )}
              {canAccess(user, "audit") && (
                <button
                  type="button"
                  onClick={() => { setOpenMenu(null); nav("/app/auditoria"); }}
                  data-testid="account-menu-audit"
                  className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5"
                >
                  <ClockCounterClockwise size={14} className="text-slate-500" />
                  Auditoria
                </button>
              )}
              {canAccess(user, "financial") && (
                <button
                  type="button"
                  onClick={() => { setOpenMenu(null); nav("/app/financeiro"); }}
                  data-testid="account-menu-financeiro"
                  className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5"
                >
                  <Wallet size={14} className="text-slate-500" />
                  Financeiro
                </button>
              )}
              <button
                type="button"
                onClick={() => { setOpenMenu(null); replayOnboardingTour(); }}
                data-testid="account-menu-replay-tour"
                className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5"
              >
                <Compass size={14} className="text-slate-500" />
                Refazer tour
              </button>
              <div className="border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => { setOpenMenu(null); logout(); nav("/login"); }}
                  data-testid="account-menu-logout"
                  className="w-full px-4 py-2.5 text-left text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2.5"
                >
                  <SignOut size={14} />
                  Sair
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
